import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { FiAward, FiHeart, FiGlobe, FiUsers } from 'react-icons/fi';

const Filosofia = () => {
  const navigate = useNavigate();
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const coreValues = [
    {
      icon: <FiAward className="w-8 h-8" />,
      title: "Excelencia",
      description: "Nos comprometemos a ofrecer solo experiencias que cumplan con nuestros altos estándares de calidad."
    },
    {
      icon: <FiHeart className="w-8 h-8" />,
      title: "Pasión",
      description: "Amamos lo que hacemos y eso se refleja en cada detalle de nuestras experiencias."
    },
    {
      icon: <FiGlobe className="w-8 h-8" />,
      title: "Sostenibilidad",
      description: "Promovemos el turismo responsable que beneficia a las comunidades locales y protege el medio ambiente."
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: "Enfoque humano",
      description: "Las personas son el corazón de nuestro negocio, tanto nuestros clientes como nuestros colaboradores."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-green-900 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Nuestra Filosofía
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-2xl">
              Descubre los valores y principios que guían cada experiencia que ofrecemos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            className="bg-white rounded-xl shadow-xl p-8 md:p-12"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Nuestra Misión</h2>
              <p className="text-xl text-gray-600 mb-8">
                Transformar la manera en que las personas experimentan el mundo, conectando a viajeros exigentes con experiencias auténticas, exclusivas y memorables que van más allá del turismo convencional.
              </p>
              <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-8"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Nuestra Visión</h2>
              <p className="text-xl text-gray-600">
                Ser reconocidos como el referente global en experiencias de viaje premium, siendo la primera opción para quienes buscan autenticidad, exclusividad y servicio excepcional en cada destino.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-6">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12"
              variants={fadeInUp}
            >
              Nuestros Valores Fundamentales
            </motion.h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow"
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                >
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-600">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            className="grid md:grid-cols-2 gap-12 items-center"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Nuestro Enfoque</h2>
              <p className="text-gray-600 mb-4">
                En Experiencias Premium, creemos que los viajes deben ser más que solo visitar lugares; deben ser transformadores. Cada experiencia que ofrecemos está cuidadosamente seleccionada para:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="bg-blue-500 rounded-full w-2 h-2 mt-2 mr-3"></span>
                  <span className="text-gray-600">Conectar a los viajeros con la esencia auténtica de cada destino</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 rounded-full w-2 h-2 mt-2 mr-3"></span>
                  <span className="text-gray-600">Apoyar a las comunidades locales y prácticas sostenibles</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 rounded-full w-2 h-2 mt-2 mr-3"></span>
                  <span className="text-gray-600">Ofrecer acceso exclusivo a lugares y eventos no disponibles para el público general</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 rounded-full w-2 h-2 mt-2 mr-3"></span>
                  <span className="text-gray-600">Crear recuerdos que perduren mucho después de que termine el viaje</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/servicios')}
                className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
              >
                Descubre nuestras experiencias
              </button>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="relative">
              <div className="bg-gray-300 h-96 rounded-xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  Imagen representativa de nuestro enfoque
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-green-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Conoce a nuestro equipo</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Detrás de cada experiencia hay un equipo apasionado de expertos en viajes dedicados a hacer de tu aventura algo extraordinario.
            </p>
            <button
              onClick={() => navigate('/equipo')}
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-3 rounded-lg text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              Conoce al equipo
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 pt-16 pb-8">
        {/* ... (usa el mismo footer que tienes en tu página principal) ... */}
      </footer>
    </div>
  );
};

export default Filosofia;